import { apiRequest, clearAuthSession, getAuthSession, saveAuthSession } from './api';

const endpointConfig = {
  login: process.env.REACT_APP_AUTH_LOGIN_PATH || '/api/auth/login',
  register: process.env.REACT_APP_AUTH_REGISTER_PATH || '/api/auth/register',
  me: process.env.REACT_APP_AUTH_ME_PATH || '/api/auth/me',
  bookings: process.env.REACT_APP_USER_BOOKINGS_PATH || '/api/me/bookings',
  media: process.env.REACT_APP_ADMIN_MEDIA_PATH || '/api/media-assets',
};

// Last-resort fallback when both the real /api/media-assets endpoint and the
// activity-derived media list are unavailable. Intentionally empty — it must
// never hardcode third-party provider images/URLs (see [[owa-no-provider-exposure]]).
const demoMediaLibrary = [];

function pickFirstObject(...candidates) {
  return candidates.find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate)) || null;
}

function normaliseUser(payload) {
  const source = pickFirstObject(payload?.user, payload?.data?.user, payload?.profile, payload?.data, payload);
  if (!source) {
    return null;
  }

  return {
    id: source.id || source.user_id || source.uuid || 'current-user',
    name: source.name || source.full_name || source.first_name || 'Usuario OWA',
    email: source.email || '',
    phone: source.phone || source.mobile || '',
    role: source.role || source.account_type || 'customer',
    company: source.company || source.organization || '',
  };
}

function extractToken(payload) {
  return (
    payload?.token ||
    payload?.access_token ||
    payload?.authToken ||
    payload?.data?.token ||
    payload?.data?.access_token ||
    ''
  );
}

function persistSession(payload) {
  const session = saveAuthSession({
    token: extractToken(payload),
    user: normaliseUser(payload),
  });

  return {
    token: session.token,
    user: session.user,
    raw: payload,
  };
}

function normaliseBookings(payload) {
  const rawItems = Array.isArray(payload)
    ? payload
    : payload?.items || payload?.results || payload?.bookings || payload?.data || [];

  return rawItems.map((item, index) => ({
    id: item.id || item.booking_id || item.reference || `booking-${index + 1}`,
    title: item.activity_title || item.title || item.activity?.title || 'Reserva OWA',
    status: item.status || 'pending',
    date: item.service_date || item.date || item.start_date || '',
    time: item.time_slot || item.time || '',
    guests: item.guests || item.seats || item.quantity || item.participants || 0,
    amount: Number(item.amount || item.total || item.price || 0),
    image: item.image || item.cover || item.activity?.image || '',
    location: item.location || item.activity?.location || '',
  }));
}

function normaliseMedia(payload) {
  const rawItems = Array.isArray(payload)
    ? payload
    : payload?.items || payload?.results || payload?.assets || payload?.data || [];

  return rawItems.map((item, index) => ({
    id: item.id || item.asset_id || item.slug || `asset-${index + 1}`,
    title: item.title || item.name || item.filename || `Asset ${index + 1}`,
    alt: item.alt || item.alt_text || '',
    category: item.category || item.collection || 'library',
    status: item.status || 'published',
    url: item.url || item.src || item.original_url || '',
    thumb: item.thumb || item.thumbnail_url || item.preview_url || item.url || item.src || '',
    width: Number(item.width || 0),
    height: Number(item.height || 0),
    updated_at: item.updated_at || item.updatedAt || item.created_at || '',
    tags: Array.isArray(item.tags) ? item.tags : [],
  }));
}

function isPendingEndpoint(error) {
  return error?.status === 404 || error?.status === 405 || error?.status === 501;
}

export function getPortalEndpoints() {
  return endpointConfig;
}

export function getCurrentSession() {
  return getAuthSession();
}

export async function loginUser(credentials) {
  const payload = await apiRequest(endpointConfig.login, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });

  return persistSession(payload);
}

export async function subscribeNewsletter(email, name, lang) {
  return apiRequest('/api/newsletter/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email, name: name || null, lang: lang || 'es' }),
  });
}

export async function fetchSocialConfig() {
  try {
    return await apiRequest('/api/auth/social-config');
  } catch {
    return { google_client_id: '', facebook_app_id: '' };
  }
}

export async function loginWithGoogle(credential) {
  const payload = await apiRequest('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
  return persistSession(payload);
}

export async function loginWithFacebook(accessToken) {
  const payload = await apiRequest('/api/auth/facebook', {
    method: 'POST',
    body: JSON.stringify({ access_token: accessToken }),
  });
  return persistSession(payload);
}

export async function registerUser(accountData) {
  const registrationPayload = {
    email: accountData.email,
    password: accountData.password,
    phone: accountData.phone,
    full_name: accountData.name,
  };

  const payload = await apiRequest(endpointConfig.register, {
    method: 'POST',
    body: JSON.stringify(registrationPayload),
  });

  return persistSession(payload);
}

export async function fetchCurrentUser() {
  const payload = await apiRequest(endpointConfig.me, { auth: true });
  const user = normaliseUser(payload);

  if (user) {
    saveAuthSession({
      token: getAuthSession().token,
      user,
    });
  }

  return user;
}

export async function fetchMyBookings() {
  const payload = await apiRequest(endpointConfig.bookings, { auth: true });
  return normaliseBookings(payload);
}

// Build media assets from the images that activities actually use (hero image +
// gallery), so the media library reflects the real content in the DB even though
// there is no separate upload pipeline yet.
async function fetchActivityMedia() {
  try {
    const res = await apiRequest('/api/activities', { query: { limit: 200 } });
    const items = res?.items || [];
    const seen = new Set();
    const media = [];
    items.forEach((a) => {
      const title = (a.title && typeof a.title === 'object' ? a.title.es : a.title) || a.slug || '';
      const urls = [a.image, ...(Array.isArray(a.gallery) ? a.gallery : [])].filter(Boolean);
      urls.forEach((url, i) => {
        if (seen.has(url)) return;
        seen.add(url);
        media.push({
          id: `act:${url}`,
          title: i === 0 ? title : `${title} · ${i + 1}`,
          alt: title,
          category: a.category || 'actividad',
          status: 'published',
          url,
          thumb: url,
          width: 0,
          height: 0,
          updated_at: a.updated_at || '',
          tags: [a.island, a.category].filter(Boolean),
        });
      });
    });
    return media;
  } catch {
    return [];
  }
}

function mergeMediaByUrl(...lists) {
  const seen = new Set();
  const out = [];
  lists.forEach((list) => {
    (list || []).forEach((item) => {
      if (!item.url || seen.has(item.url)) return;
      seen.add(item.url);
      out.push(item);
    });
  });
  return out;
}

export async function fetchMediaLibrary() {
  const activityMedia = await fetchActivityMedia();
  try {
    const payload = await apiRequest(endpointConfig.media, { auth: true });
    const apiItems = normaliseMedia(payload);
    return {
      items: mergeMediaByUrl(apiItems, activityMedia),
      source: activityMedia.length ? 'actividades + api' : 'api',
    };
  } catch (error) {
    if (isPendingEndpoint(error)) {
      return activityMedia.length
        ? { items: activityMedia, source: 'actividades' }
        : { items: demoMediaLibrary, source: 'demo' };
    }
    if (activityMedia.length) {
      return { items: activityMedia, source: 'actividades' };
    }
    throw error;
  }
}

// Upload an actual image file to the server (stored on disk, served from /uploads/).
export async function uploadMediaFile(file, { title = '', alt = '', category = 'library' } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title || file.name.replace(/\.[^.]+$/, ''));
  formData.append('alt_text', alt);
  formData.append('kind', category || 'library');

  return apiRequest('/api/admin/media/upload', {
    method: 'POST',
    body: formData,
    auth: true,
  });
}

export async function createMediaAsset(input) {
  const { file, ...fields } = input;
  const payload = {
    title: fields.title,
    url: fields.source_url,
    kind: fields.category || 'library',
    alt_text: fields.alt || '',
    tags: fields.category ? [fields.category] : [],
    metadata: {
      source: 'remote',
      submitted_via: 'admin-media',
    },
  };

  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
      }
    });

    return apiRequest(endpointConfig.media, {
      method: 'POST',
      body: formData,
      auth: true,
    });
  }

  return apiRequest(endpointConfig.media, {
    method: 'POST',
    body: JSON.stringify(payload),
    auth: true,
  });
}

export async function updateMediaAsset(assetId, fields) {
  const payload = {
    ...(fields.title ? { title: fields.title } : {}),
    ...(fields.source_url ? { url: fields.source_url } : {}),
    ...(fields.category ? { kind: fields.category } : {}),
    ...(fields.alt ? { alt_text: fields.alt } : {}),
    ...(fields.featured !== undefined ? { metadata: { featured: Boolean(fields.featured) } } : {}),
  };

  return apiRequest(`${endpointConfig.media}/${encodeURIComponent(assetId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    auth: true,
  });
}

export function logoutUser() {
  clearAuthSession();
}
